import { EditOutlined } from '@ant-design/icons'
import { Button, Card, Col, Flex, message, Row } from 'antd'
import dayjs from 'dayjs'
import { BoosterProgramDetail, BoosterProgramFormValues } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import {
  BOOSTER_ENROLLMENT_PERIOD_UNIT_CONFIG,
  BOOSTER_PROGRAM_TARGET_USER_CONFIG,
} from 'constants/booster-program'
import { downloadBlob } from 'utils/download-file'
import { logError } from 'utils/logger'

import { HorizontalRow } from '../form-create/horizontal-row'

import { ProgramSettingsEditForm } from './program-settings-edit-form'

interface Props {
  data: BoosterProgramDetail
  onSave?: (values: BoosterProgramFormValues) => Promise<void> | void
}

export const BoosterProgramSettingsView = ({ data, onSave }: Props) => {
  const [editing, setEditing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const canEdit = data.status === 'SCHEDULED'

  const handleExportWhitelist = async () => {
    if (!data.id || exporting) return
    setExporting(true)
    try {
      const res = await AppAdmin.exportBoosterProgramWhitelist(data.id)
      downloadBlob(res.data, data.whitelistFileName || `whitelist_${data.id}.csv`)
    } catch (err) {
      logError('Export booster whitelist failed', err)
      message.error('Failed to export whitelist')
    } finally {
      setExporting(false)
    }
  }

  if (editing && onSave) {
    return (
      <ProgramSettingsEditForm
        data={data}
        onCancel={() => setEditing(false)}
        onSave={async (values) => {
          await onSave(values)
          setEditing(false)
        }}
      />
    )
  }

  const startDate = dayjs(data.period.start).format('MMM D, YYYY HH:mm')
  const endDate = dayjs(data.period.end).format('MMM D, YYYY HH:mm')

  return (
    <Card className="booster-detail__settings-card">
      <Flex justify="space-between" align="center" className="booster-detail__settings-header">
        <span className="booster-detail__section-title">Program Settings</span>
        {canEdit && (
          <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </Flex>

      <HorizontalRow label="Program Title">
        <div className="booster-detail__value">{data.title}</div>
      </HorizontalRow>

      <HorizontalRow label="Program Period">
        <Flex gap={8} align="center">
          <div className="booster-detail__value">{startDate}</div>
          <span className="booster-program-create__arrow">→</span>
          <div className="booster-detail__value">{endDate}</div>
        </Flex>
      </HorizontalRow>

      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <div className="booster-detail__field-label">Description</div>
          <MarkdownEditor disabled value={data.description} placeholder="No description" />
        </Col>
        <Col xs={24} md={12}>
          <div className="booster-detail__field-label">Terms &amp; Conditions</div>
          <MarkdownEditor disabled value={data.terms} placeholder="No terms & conditions" />
        </Col>
      </Row>

      <HorizontalRow label="User Enrollment Period">
        <Flex gap={8} align="center">
          <div className="booster-detail__value">{data.enrollmentPeriodValue}</div>
          <span className="booster-program-create__hint-inline">
            {BOOSTER_ENROLLMENT_PERIOD_UNIT_CONFIG[data.enrollmentPeriodUnit]} (from each
            user&apos;s enrollment date)
          </span>
        </Flex>
      </HorizontalRow>

      <HorizontalRow label="Target Users">
        <div className="booster-detail__value">
          {BOOSTER_PROGRAM_TARGET_USER_CONFIG[data.targetUsers]}
        </div>
      </HorizontalRow>

      {data.whitelistFileName && (
        <div className="booster-program-create__upload">
          <a
            className="booster-program-create__file-chip booster-detail__file-chip-link"
            onClick={handleExportWhitelist}
            aria-disabled={exporting}
          >
            {data.whitelistFileName.split('/').pop() || data.whitelistFileName}
          </a>
        </div>
      )}

      <HorizontalRow label="Participant Limit">
        <div className="booster-detail__value">
          {data.participantLimitType === 'LIMITED' ? 'Limited' : 'Unlimited'}
        </div>
      </HorizontalRow>

      <div className="booster-program-create__inline-row">
        {data.participantLimitType === 'LIMITED' && (
          <Flex gap={8} align="center">
            <span className="booster-program-create__inline-label">Limit Number</span>
            <div className="booster-detail__value">
              {new Intl.NumberFormat('en-US').format(data.participantLimit ?? 0)}
            </div>
          </Flex>
        )}
        <Flex gap={8} align="center">
          <span className="booster-program-create__inline-label">Starting Fill Number</span>
          <div className="booster-detail__value">{data.startingFillNumber ?? 0}</div>
        </Flex>
        <Flex gap={8} align="center">
          <span className="booster-program-create__inline-label">Increase by</span>
          <div className="booster-detail__value">{data.increaseByPercent ?? 0}</div>
          <span className="booster-program-create__inline-label">% every</span>
          <div className="booster-detail__value">{data.increaseEvery ?? 0}</div>
          <span className="booster-program-create__inline-label">
            {data.increaseEveryUnit ?? 'days'}
          </span>
        </Flex>
      </div>
    </Card>
  )
}
